import { AMOwnershipParcelComplete } from 'src/common-services'
import {
  MAX_CONTENT_WIDTH,
  SidebarResultOverlay,
  SidebarResultOverlayProps,
} from 'src/v2/components/sidebar-result-overlay'
import { SidebarResultOverlayHeader } from 'src/v2/components/sidebar-result-overlay-header'
import { Colors, Shapes } from 'src/components/theme'
import styled from '@emotion/styled'
import { LocationOn } from '@emotion-icons/material/LocationOn'
import { DedupeResults } from 'src/views/map-explorer/sidebar/related-results/types'
import { PlacesResultsList } from 'src/views/map-explorer/sidebar/related-results/places-result-list'
import dynamic from 'next/dynamic'
import * as coreAtoms from 'src/v2/state/core'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

// As the map is being rendered in a Popper it errors out with next js server side rendering.
// So we need to explicitly render the map on the client.
const NearbyPlacesMap = dynamic(
  () => import('src/views/map-explorer/sidebar/related-results/nearby-places-map').then((mod) => mod.NearbyPlacesMap),
  {
    ssr: false,
  }
)

const ParcelResultOverlayContent = styled.div`
  width: ${MAX_CONTENT_WIDTH}px;
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[2]}px;
`

type NearbyPlacesOverlayProps = {
  parcel?: AMOwnershipParcelComplete
  anchorEl: SidebarResultOverlayProps['anchorEl']
  onClose: SidebarResultOverlayProps['onClose']
  open: SidebarResultOverlayProps['open']
  nearbyResults?: DedupeResults
  radius?: number
}

export const NearbyPlacesOverlay: React.FC<NearbyPlacesOverlayProps> = ({
  anchorEl,
  open,
  onClose,
  nearbyResults,
  radius = 0.1,
}) => {
  const latLng = useAtomValue(coreAtoms.selectedEntityLatLng)

  const center = useMemo<GeoJSON.Point | undefined>(() => {
    if (!latLng) {
      return undefined
    }
    return {
      type: 'Point',
      coordinates: latLng,
    }
  }, [latLng])

  return (
    <SidebarResultOverlay
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      header={
        <SidebarResultOverlayHeader
          title="Nearby Places"
          subtitle={`Inside a ${radius.toFixed(1)} mile radius (or up to 1 mile)`}
          icon={<LocationOn size={18} />}
          iconShape="square"
          iconBgColor={Colors.primaryBlue600}
          iconBorderColor={Colors.primaryBlue800}
          iconSize={40}
        />
      }>
      <ParcelResultOverlayContent>
        {center && <NearbyPlacesMap results={nearbyResults} radius={radius} center={center} />}
        <PlacesResultsList results={nearbyResults} />
      </ParcelResultOverlayContent>
    </SidebarResultOverlay>
  )
}
